"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useCallback, type ReactNode } from "react";
import { Check } from "lucide-react";
import { Category } from "@/types";
import { cn } from "@/lib/utils";

interface ProductFiltersProps {
  categories: Category[];
  categorySlug?: string;
  /** true olduğunda kenar çizgisi ve min-h-full kaldırılır (drawer içi için) */
  inDrawer?: boolean;
  onFilterChange?: () => void;
}

export function ProductFilters({ categories, categorySlug, inDrawer = false, onFilterChange }: ProductFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentCategory = searchParams.get("category") ?? categorySlug ?? "";
  const minPrice = searchParams.get("minPrice") ?? "";
  const maxPrice = searchParams.get("maxPrice") ?? "";
  const onSale = searchParams.get("onSale") === "true";
  const inStock = searchParams.get("inStock") !== "false";

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      const params = new URLSearchParams(searchParams.toString());
      if (value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      params.delete("page");
      let targetPath = pathname;
      if (key === "category") {
        params.delete("category");
        targetPath = value ? `/kategori/${value}` : "/products";
      }
      const query = params.toString();
      router.push(query ? `${targetPath}?${query}` : targetPath, { scroll: false });
      onFilterChange?.();
    },
    [router, pathname, searchParams, onFilterChange]
  );

  return (
    <aside
      className={cn(
        "space-y-6",
        inDrawer
          ? ""
          : "sticky top-28 rounded-[var(--sf-radius)] border border-gray-100 bg-white/80 p-4 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04]"
      )}
    >
      {!inDrawer && (
        <div className="border-b border-gray-100 pb-4 dark:border-white/10">
          <h2 className="font-semibold tracking-tight text-gray-950 dark:text-gray-100">Filtreler</h2>
          <p className="text-xs text-gray-500 dark:text-gray-500">Ürünleri daralt</p>
        </div>
      )}

      {/* Categories */}
      <FilterSection title="Kategori" divider={false}>
        <ul className="space-y-1.5">
          <li>
            <FilterRadioRow
              label="Tümü"
              selected={!currentCategory}
              onClick={() => updateParam("category", null)}
            />
          </li>
          {categories.map((cat) => (
            <li key={cat.id}>
              <FilterRadioRow
                label={cat.name}
                count={cat.productCount}
                selected={currentCategory === cat.slug}
                onClick={() => updateParam("category", cat.slug)}
              />
            </li>
          ))}
        </ul>
      </FilterSection>

      {/* Price Range */}
      <FilterSection title="Fiyat Aralığı">
        <div className="grid grid-cols-2 gap-2">
          <PriceInput
            label="Min"
            value={minPrice}
            placeholder="0"
            onChange={(value) => updateParam("minPrice", value)}
          />
          <PriceInput
            label="Max"
            value={maxPrice}
            placeholder="500"
            onChange={(value) => updateParam("maxPrice", value)}
          />
        </div>
      </FilterSection>

      {/* Toggles */}
      <FilterSection title="Durum">
        <div className="space-y-2">
          <FilterCheckboxRow
            label="İndirimli Ürünler"
            checked={onSale}
            onChange={(checked) => updateParam("onSale", checked ? "true" : null)}
          />
          <FilterCheckboxRow
            label="Stokta Olanlar"
            checked={inStock}
            onChange={(checked) => updateParam("inStock", checked ? "true" : "false")}
          />
        </div>
      </FilterSection>
    </aside>
  );
}

function FilterSection({ title, children, divider = true }: { title: string; children: ReactNode; divider?: boolean }) {
  return (
    <section className={cn("space-y-3", divider && "border-t border-gray-100 pt-5 dark:border-white/10")}>
      <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{title}</h3>
      {children}
    </section>
  );
}

function FilterRadioRow({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count?: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex min-h-11 w-full items-center justify-between rounded-full border px-3 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--sf-accent)]",
        selected
          ? "border-[var(--sf-accent)]/45 bg-[var(--sf-accent-light)]/70 text-gray-950 dark:border-white/12 dark:bg-white/[0.07] dark:text-white"
          : "border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-950 dark:text-gray-400 dark:hover:border-white/10 dark:hover:bg-white/5 dark:hover:text-white"
      )}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <span
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
            selected ? "border-[var(--sf-accent)] bg-[var(--sf-accent)]" : "border-gray-300 group-hover:border-gray-500 dark:border-white/20"
          )}
        >
          {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
        </span>
        <span className="truncate font-medium">{label}</span>
      </span>
      {typeof count === "number" && (
        <span className={cn("ml-3 text-[11px]", selected ? "text-gray-500 dark:text-white/60" : "text-gray-400")}>{count}</span>
      )}
    </button>
  );
}

function PriceInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold text-gray-500 dark:text-gray-400">{label}</span>
      <span className="flex h-11 items-center rounded-[var(--sf-radius)] border border-gray-200 bg-white px-3 transition-colors focus-within:border-[var(--sf-primary)] focus-within:ring-2 focus-within:ring-[var(--sf-accent)]/20 dark:border-white/10 dark:bg-white/5 dark:focus-within:border-white/40">
        <input
          type="number"
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-gray-950 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-600"
        />
        <span className="text-xs font-bold text-gray-400">₺</span>
      </span>
    </label>
  );
}

function FilterCheckboxRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={cn(
        "group flex min-h-11 cursor-pointer items-center gap-3 rounded-full border px-3 text-sm transition-colors",
        checked
          ? "border-[var(--sf-accent)]/45 bg-[var(--sf-accent-light)]/70 text-gray-950 dark:border-white/12 dark:bg-white/[0.07] dark:text-white"
          : "border-gray-100 text-gray-600 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-950 dark:border-white/10 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white"
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
          checked ? "border-[var(--sf-accent)] bg-[var(--sf-accent)] text-white" : "border-gray-300 group-hover:border-gray-500 dark:border-white/20"
        )}
      >
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="font-medium">{label}</span>
    </label>
  );
}
